import React, {useEffect, useRef, useState} from 'react';
import {useAppDispatch, useAppSelector} from "app/store/hook";
import {formatDistanceToNow} from "date-fns";
import {ko} from 'date-fns/locale';
import {FirebaseService} from "app/services/common/FirebaseService";
import {getMessages} from "app/guard/authGuard";
import ArcButton, {ButtonColor} from "../../arc/ArcButton";
import moment from "moment";
import {UserInfoModel} from "../../../model/ua/user-info";
import {readAllAlarmMessages} from "../../../reducers/alarmSlice";
import {Bell} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {toAppPath} from "app/utils/alarmUtil";

export default function AccountAlarm() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userInfo: UserInfoModel = useAppSelector((state) => state.userInfo.userInfo);
  const alarmMessages: any = useAppSelector((state) => state.messagesAlarm.messagesAlarm);
  const alarmRef = useRef<HTMLLIElement | null>(null);

  /*
   * 드롭다운 여닫기. 예전에는 jQuery 스크립트(public/portal/assets/js/layout.js)를 이
   * 컴포넌트가 마운트될 때마다 body 에 다시 넣어 클릭 핸들러를 걸었다. 다시 마운트되면
   * 핸들러가 두 번 붙어 한 번 눌러도 열렸다 바로 닫혔다. 이제 React 상태로 연다.
   */
  const [open, setOpen] = useState(false);

  const handleClickAlarmBadge = (event: any) => {
    event.preventDefault();
    setOpen(v => !v);
  }

  const handleClickAlarmClose = (event: any) => {
    event.preventDefault();
    setOpen(false);
  }

  const handleClickReadAllAlarm = () => {
    dispatch(readAllAlarmMessages(alarmMessages.filter((item: any) => item.readYn === 'N')));
    FirebaseService.getInstance().readAllMessage(alarmMessages.filter((item: any) => item.readYn === 'N')).then(resp => {
      if (resp) {
        if (resp.resultCode == "Y") {
          getMessages(dispatch, userInfo);

        }
      }
    })
  }

  /**
   * 알림을 열어 그 화면으로 이동한다.
   *
   * ★ 우리 앱 안의 경로면 라우터로 이동한다. 지금까지는 무조건 window.open 이라
   *   같은 사이트의 화면도 <새 창>으로 떴다 — 알림을 누를 때마다 창이 하나씩 늘었고,
   *   새 창은 로그인 세션 복구부터 다시 시작해 느렸다.
   *   앱 밖 주소(법령 원문 링크 등)는 그대로 새 창으로 연다.
   */
  const openAlarmLink = (link: string | null | undefined) => {
    const path = toAppPath(link);
    if (path) {
      navigate(path);
    } else if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  }

  const handleClickAlarmNavigate = (event: any, item: any) => {
    event.preventDefault();
    setOpen(false);
    if (item.readYn == "N") {
      FirebaseService.getInstance().readMessage(item).then(resp => {
        if (resp) {
          if (resp.resultCode == "Y") {
            getMessages(dispatch, userInfo);
            openAlarmLink(item?.data?._link);
          }
        }
      })
    } else {
      openAlarmLink(item?.data?._link);
    }
  }

  function timeAgo(dateString: string): string {
    // need to convert utc to local
    const date = moment.utc(dateString).toDate();
    return formatDistanceToNow(date, {addSuffix: true, locale: ko});
  }

  // 바깥을 누르거나 Esc 면 닫는다
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (alarmRef.current && !alarmRef.current.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const unreadCount = alarmMessages.filter((item: { readYn: string }) => item.readYn === 'N').length;
  const badgeLabel = unreadCount >= 99 ? '99+' : String(unreadCount);
  const badgeClassName = unreadCount > 0 ? 'alarm-badge alarm-badge--active' : 'alarm-badge alarm-badge--empty';

  return (


      <li className={`alarm${open ? " active" : ""}`} ref={alarmRef}>
        <a href="#" onClick={handleClickAlarmBadge} aria-expanded={open}>
          <Bell className="util-icon" aria-hidden="true" />
          <span className="hid">알림</span>
          <em className={badgeClassName}>
            {badgeLabel}
          </em>
        </a>

        <div className="util-over-box" style={{display: open ? "block" : "none"}}>
          <div className="tit">
            <strong className="tit-txt">알림 <ArcButton type={"text"}
                                                      size={"btn-small"}
                                                      shape={"btn-round"}
                                                      color={ButtonColor.btn1}
                                                      text={"전체읽음"}
                                                      onClick={() => handleClickReadAllAlarm()}
            ></ArcButton>
            </strong>

            <a href="#" onClick={handleClickAlarmClose} className="close-btn">
              <i className="las la-times"></i><span className="hid">닫기</span>
            </a>
          </div>
          <div className="cont">
            <ul id="notify-area">
              {alarmMessages?.length === 0
                ?
                  <li className="ac">알림이 없습니다.</li>
                :
                  alarmMessages.map((item: any) => {
                    return <li key={item.messageId} className={item.readYn == "Y" ? "bgY" : "bgN"}>
                      <a href="#" onClick={event => handleClickAlarmNavigate(event, item)} title={item.title}>
                        <div className="img no-profile"></div>
                        <div className="txt">
                          <strong style={{whiteSpace: 'pre-line'}}>{item.body}</strong>
                          <span>{timeAgo((item.regDt))}</span>
                        </div>
                      </a>
                    </li>;
                  })
              }
            </ul>
          </div>
          {/*
            벨에는 최근 한 달만 담긴다. 그보다 전 알림은 알림 목록(/alarms)에서 본다 —
            메뉴로 따로 두지 않고 여기가 그 입구다.
            목록 스크롤 영역(.cont) 밖에 둔다. 안에 두었더니 최대 높이에 걸려 버튼 아래가
            잘렸고, 알림이 많으면 스크롤과 함께 밀려 보이지 않았다.
          */}
          <div className="foot">
            <ArcButton type={"text"}
                       size={"btn-small"}
                       shape={"btn-round"}
                       color={ButtonColor.btn1}
                       text={"지난 알림 보기"}
                       onClick={() => { setOpen(false); navigate("/alarms"); }}
            ></ArcButton>
          </div>
        </div>
      </li>
  )
}